import React, { useMemo } from 'react'
import { PieChart, Pie, Cell } from 'recharts'
import { useIsMobile } from '../../../hooks/use-mobile'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../components/ui/chart'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip'

const SecretsDistribution = () => {
  const isMobile = useIsMobile();
  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  // Dummy data - Active and Unvalidated secrets
  const verifiedCount = 1240;
  const unverifiedCount = 820;
  const totalSecrets = verifiedCount + unverifiedCount;

  // Memoize chart data for Recharts
  const chartData = useMemo(() => [
    {
      name: 'Active',
      value: verifiedCount,
      fill: '#EF4444'
    },
    {
      name: 'Unvalidated',
      value: unverifiedCount,
      fill: '#EAB308'
    }
  ], [verifiedCount, unverifiedCount]);

  // Create empty chart data when no secrets  
  const emptyChartData = useMemo(() => [
    {
      name: 'No Data',
      value: 1, // Use small value to ensure chart renders
      fill: '#E5E7EB' // Light gray for empty state
    }
  ], []);

  // Use empty data if no secrets
  const displayChartData = totalSecrets === 0 ? emptyChartData : chartData;

  // Helper function to adjust color brightness for hover effect
  const adjustColorBrightness = (color, isHovered) => {
    if (!isHovered) return color;
    
    // For red color (Active), make it slightly lighter
    if (color === '#EF4444') {
      return '#F87171';
    }
    
    // For yellow color (Unvalidated), make it slightly lighter
    if (color === '#EAB308') {
      return '#FDE047';
    }
    
    return color;
  };

  // Chart configuration for shadcn/ui tooltips
  const chartConfig = {
    value: {
      label: "Secrets",
    },
    ACTIVE: {
      label: "Active",
      color: "#EF4444",
    },
    UNVALIDATED: {
      label: "Unvalidated", 
      color: "#EAB308",
    },
  };

  return (
    <TooltipProvider>
      <Card className={`h-full bg-white border-[#CBC8C89C] flex flex-col relative ${isMobile ? 'rounded-xl p-2' : 'rounded-2xl p-3'}`}>
        <CardHeader className={`pb-0 p-0 ${isMobile ? 'flex justify-center' : 'flex flex-row justify-between items-center'}`}>
          <CardTitle className={`font-semibold text-black font-inter tracking-[-0.025em] ${isMobile ? 'text-sm' : 'text-lg'}`}>
            Secrets Distribution
          </CardTitle>
        </CardHeader>
        <CardContent className={`flex-1 flex justify-center items-center overflow-x-hidden p-0 ${isMobile ? 'min-h-[160px] pb-2' : 'min-h-[150px]'}`}>
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square"
          style={{ width: isMobile ? 140 : 240, height: isMobile ? 140 : 240 }}
        >
          <PieChart>
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent 
                hideLabel 
                className="bg-white border border-gray-200 shadow-lg backdrop-blur-none w-40"
              />}
            />
            <Pie
              data={displayChartData}
              dataKey="value"
              nameKey="name"
              innerRadius={isMobile ? 40 : 75}
              outerRadius={isMobile ? 60 : 105}
              startAngle={90}
              endAngle={-270}
              strokeWidth={0}
              onMouseEnter={totalSecrets > 0 ? (_, index) => setHoveredIndex(index) : undefined}
              onMouseLeave={totalSecrets > 0 ? () => setHoveredIndex(null) : undefined}
              isAnimationActive={false}
            >
              {displayChartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={totalSecrets === 0 ? entry.fill : adjustColorBrightness(entry.fill, hoveredIndex === index)} 
                />
              ))}
            </Pie>
            <text
              x="50%"
              y="45%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: isMobile ? "16px" : "24px", fontWeight: "600", fill: totalSecrets === 0 ? "#9CA3AF" : "#000" }}
            >
              {totalSecrets}
            </text>
            <text
              x="50%"
              y="55%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: isMobile ? "9px" : "12px", fill: totalSecrets === 0 ? "#9CA3AF" : "#8D82AA" }}
            >
              {totalSecrets === 0 ? "No Secrets" : "Total Secrets"}
            </text>
          </PieChart>
        </ChartContainer>
      </CardContent>
      
      
      <Tooltip>
        <TooltipTrigger asChild>
          <button className={`absolute bottom-3 right-3 w-5 h-5 rounded-full border border-black/35 bg-white flex items-center justify-center text-black/35 hover:bg-gray-50 transition-colors ${isMobile ? 'text-xs' : 'text-sm'}`}>
            <span className="font-semibold">i</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-2">
            <p className="text-sm font-medium">Secrets Distribution</p>
            <div className="space-y-1">
              {chartData.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <div
                    className="w-2 h-2 rounded-sm"
                    style={{ backgroundColor: item.fill }}
                  />
                  <span>{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-600 pt-1 border-t">
              Total: {totalSecrets} secrets
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </Card>
    </TooltipProvider>
  );
};

export default SecretsDistribution;